import { useCallback, useEffect, useState } from "react"
import { IHomeTableEquipmentTable } from "../../../../app/stores/homeTableEquipmentStore"
import { HomeProgressBar } from "../../common/HomeProgressBar"
import { createHomeEquipmentData, HomeEquipmentData } from "../HomeTableEquipmentTable"

export type IHomeTableFilterSession = 'tagNumber' | 'systemName' | 'equipmentType' | 'probabilityOfSuccess' | 'availability' | 'maintainability';

export type MinMax = {
    min: number | null;
    max: number | null;
}

type IFilter = {
    tagNumber: string[];
    systemName: string[];
    equipmentType: string[];
    probabilityOfSuccess: MinMax
    availability: MinMax;
    maintainability: MinMax;
}

const filterInit: IFilter = {
    tagNumber: [''],
    systemName: [''],
    equipmentType: [],
    availability: {
        min: null,
        max: null,
    },
    maintainability: {
        min: null,
        max: null,
    },
    probabilityOfSuccess: {
        min: null,
        max: null,
    },
}

const isProgressFilter = (obj: any): obj is MinMax  => {
    return 'min' in obj
}

export function useTable(list: IHomeTableEquipmentTable[]) {
    const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false)
    const [rows, setRows] = useState<HomeEquipmentData[]>([])
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [filter, setFilter] = useState<IFilter>(filterInit)
    const [equipmentType, setEquipmentType] = useState<string[]>([])
    
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleRows = useCallback((item: IHomeTableEquipmentTable) => {
        setRows(prev => 
            [
                ...prev, 
                createHomeEquipmentData(
                    item.tagNumber, 
                    item.systemName, 
                    item.equipmentType, 
                    <HomeProgressBar mx="auto" value={item.probabilityOfSuccess} />, 
                    <HomeProgressBar mx="auto" value={item.availability} />, 
                    <HomeProgressBar mx="auto" value={item.maintainability} />,
                )
            ]
        )
        return () => setRows([])
    }, [setRows])
    
    useEffect(() => {
        const type = list.map(i => i.equipmentType)
        const datasets = new Set(type)
        
        setEquipmentType([...datasets as any])

        return () => setEquipmentType([])
    }, [list, handleRows, setEquipmentType])

    // filter
    const handleFilter = (column: IHomeTableFilterSession, value: MinMax | string) => {
        if (typeof filter[column] === 'object' && Array.isArray(filter[column])) {
            setFilter({
                ...filter,
                [column]: [value]
            })
        }
        else {
            setFilter({
                ...filter,
                [column]: value
            })
        }
    }

    const handleClear = (column: IHomeTableFilterSession) => {
        if (column === 'systemName' || column === 'tagNumber') {
            setFilter({
                ...filter,
                [column]: ['']
            })
        } else {
            setFilter({
                ...filter,
                [column]: isProgressFilter(filter[column]) ? filterInit.availability: []
            })
        }
    }

    const handleClearAll = () => setFilter(filterInit)

    const buildFilter = (filter: IFilter) => {
        let query = {};
        for (let keys in filter) {
            if ( ((filter as any)[keys].constructor === Object) || ((filter as any)[keys].constructor === Array && (filter as any)[keys].length > 0)) {
                (query as any)[keys] = (filter as any)[keys];
            }
        }
        return query;
    };
    
    const filterData = (data: IHomeTableEquipmentTable[], query: any) => {
        const keysWithMinMax = [
            'maintainability',
            'availability',
            'probabilityOfSuccess'
        ];
        const keysWithString = [
            'tagNumber',
            'systemName',
        ]

        const filteredData = data.filter((item) => {
            for (let key in query) {
                if (keysWithMinMax.includes(key)) {
                    if (query[key]['min'] !== null && (item as any)[key] < query[key]['min']) {
                        return false;
                    } 
                    if (query[key]['max'] !== null && (item as any)[key] > query[key]['max']) {
                        return false;
                    }
                }
                else if (keysWithString.includes(key)) {
                    if (!(item as any)[key].toLowerCase().includes(query[key][0].toLowerCase())) {
                        return false
                    }
                }
                else if (!query[key].includes((item as any)[key])) {
                    return false;
                }
            }
            return true;
        })
        return filteredData;
    }

    useEffect(() => {
        if (filter.availability.min !== null || filter.availability.max !== null
            || filter.maintainability.min !== null || filter.maintainability.max !== null
            || filter.probabilityOfSuccess.min !== null || filter.probabilityOfSuccess.max !== null
            || filter.equipmentType.length > 0
            || filter.systemName[0]?.length > 0
            || filter.tagNumber[0]?.length > 0) {
                setIsFilterApplied(true)
            } else {
                setIsFilterApplied(false)
            }
        
        const query = buildFilter(filter);
        
        const result = filterData(list, query);

        result.forEach(i => handleRows(i))
            
        return () => setRows([])
    }, [filter, handleRows, list])
    
    return { 
        rows, 
        page, 
        rowsPerPage, 
        handleChangePage, 
        handleChangeRowsPerPage, 
        handleClearAll, 
        handleClear, 
        handleFilter,
        filter, 
        equipmentType,
        isFilterApplied,
    }
}
